import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { getStatisticalData } from './useReportData';
import MainContainer from './MainContainer';
import useAgentEvents from '../../../Monitoring/Queue/MonitoringScreen/hooks/useAgentEvents';
import DialerService from '../../service';

/**
 * DialerDashboard component fetches and displays dialer statistics, pending dialers, and last dialers.
 * It also filters and sorts agents based on their queue and extension.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <DialerDashboard />
 *
 * @function
 * @name DialerDashboard
 *
 * @description
 * This component fetches dialer statistics, pending dialers, and last dialers from the server.
 * It also filters and sorts agents based on their queue and extension.
 * The data is refreshed every 20 seconds.
 *
 * @property {string} dialerName - The name of the dialer extracted from the URL query string.
 * @property {string} NewDialerName - The new name of the dialer extracted from the URL query string.
 * @property {string} dialerQueueName - The name of the dialer queue extracted from the URL query string.
 * @property {Object} id - The id of the dialer extracted from the URL parameters.
 * @property {Array} agents - The list of agents fetched from the useAgentEvents hook.
 * @property {Object} dialerStatistics - The statistics of the dialer.
 * @property {Array} pendingDialer - The list of pending dialers.
 * @property {Array} lastDialers - The list of last dialers.
 *
 * @method orderByExtension
 * @description Sorts the items by their extension in ascending order.
 * @param {Array} items - The list of items to be sorted.
 * @returns {Array} The sorted list of items.
 *
 * @method orderByQueue
 * @description Sorts the items by their queue name in ascending order.
 * @param {Array} items - The list of items to be sorted.
 * @returns {Array} The sorted list of items.
 *
 * @method filterAgentsByQueue
 * @description Filters the agents by their queue name.
 * @param {Array} items - The list of agents to be filtered.
 * @returns {Array} The filtered list of agents.
 *
 * @method getPendingDialerById
 * @description Fetches the pending dialer by its id.
 * @param {string} dialerId - The id of the dialer.
 * @returns {Promise<Object>} The pending dialer data.
 *
 * @useEffect
 * @description Fetches the dialer statistics and sets up an interval to refresh the data every 20 seconds.
 *
 * @useEffect
 * @description Fetches the pending dialers and last dialers.

 * This component uses several hooks:
 * - `useParams` to get the dialer ID from the URL.
 * - `useAgentEvents` to get the list of agents.
 * - `useState` to manage the state of dialer statistics, pending dialers, and last dialers.
 * - `useEffect` to fetch data when the component mounts and at regular intervals.
 *
 * @returns {JSX.Element} The rendered component.
 */

const DialerDashboard = () => {
  const dialerName = window.location.search.split('?')[1];
  const NewDialerName = dialerName.split('=')[1];
  const dialerQueueName = window.location.search.split('=')[2];
  const { id } = useParams();
  const { agents } = useAgentEvents();
  const [dialerStatistics, setDialerStatistics] = useState({});
  const [pendingDialer, setPendingDialer] = useState([]);
  const [lastDialers, setLastDialers] = useState([]);

  const orderByExtension = (items) => {
    return [...items].sort((a, b) => {
      return a.extension < b.extension ? -1 : 1;
    });
  };

  const orderByQueue = (items) => {
    return [...items].sort((a, b) => {
      return a.queueName < b.queueName ? -1 : 1;
    });
  };

  const filterAgentsByQueue = (items) => {
    const listFilterAgents = items.filter(
      (item) => item.queueName === dialerQueueName
    );
    return listFilterAgents;
  };

  const getPendingDialerById = async (dialerId) => {
    return await DialerService.getPendingDialerById(dialerId);
  };

  useEffect(() => {
    async function getReportData() {
      const data = await getStatisticalData({ dialerId: id });
      setDialerStatistics(data.data.listDiscadorDetailedSyntheticDTO[0]);
    }

    async function fetchPendingDialer(id) {
      const data = await getPendingDialerById(id);
      setPendingDialer(data.data.data);
    }

    async function fetchLastDialers(id) {
      const data = await DialerService.getLastDialers(id);
      setLastDialers(data.data.data);
    }

    async function fetchData() {
      await Promise.all([
        getReportData(),
        fetchPendingDialer(id),
        fetchLastDialers(id)
      ]);
    }

    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 1000 * 20);

    return () => clearInterval(interval);
  }, [id]);

  return (
    <MainContainer
      data={{
        ...dialerStatistics,
        NewDialerName: NewDialerName,
        agents: orderByExtension(orderByQueue(filterAgentsByQueue(agents))),
        pendingDialer,
        lastDialers
      }}
    />
  );
};

export default DialerDashboard;
